import React, { useState, Fragment } from "react";
import Card from "../../../../components/ui/Card";
import Button from "../../../../components/ui/Button";
import {
  PlusCircle,
  MoreVertical,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import CreateCampaignModal from "../modals/CreateCampaignModal";
import { Menu, Transition } from "@headlessui/react";
import { mockCreator, platformAdOpportunities } from "../../../../data/mockData";
import RecentActivityList from "../../../../components/ui/RecentActivityList";

const AdCampaignsTab = ({ campaigns: initialCampaigns, onCreate, onEdit }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [isDirectSaleEnabled, setIsDirectSaleEnabled] = useState(false);

  const handleRowExpand = (campaignId) => {
    setExpandedRow(expandedRow === campaignId ? null : campaignId);
  };

  const totalRevenue = initialCampaigns.reduce(
    (acc, camp) =>
      acc +
      (camp.creatives || []).reduce(
        (sum, creative) => sum + (creative.revenueETB || 0),
        0
      ),
    0
  );
  const activeCampaignsCount = initialCampaigns.filter(
    (c) => c.status === "Active"
  ).length;
  const overallCTR = "3.2%";

  const adTransactions = mockCreator.mockTransactions
    .filter((txn) => txn.type === "Ad Revenue")
    .slice(0, 5);

  const handleApply = (opportunity) => {
    alert(`Application sent for ${opportunity.campaignName}! The advertiser will review your profile.`);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Analytics Cards */}
        <div className="grid grid-cols-1 @md:grid-cols-3 gap-4">
          <Card>
            <p className="text-sm text-slate-500">Total Ad Revenue</p>
            <p className="text-2xl font-bold text-slate-800">
              {totalRevenue.toLocaleString()} ETB
            </p>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Active Campaigns</p>
            <p className="text-2xl font-bold text-slate-800">
              {activeCampaignsCount}
            </p>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Overall Click-Through Rate</p>
            <p className="text-2xl font-bold text-slate-800">{overallCTR}</p>
          </Card>
        </div>

        {/* Platform Opportunities Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            Platform Opportunities
          </h3>
          <div className="grid grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 gap-4">
            {platformAdOpportunities.map((opp) => (
              <Card key={opp.id} className="flex flex-col h-full">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-slate-800">{opp.advertiser}</h4>
                  <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded">
                    {opp.revenueModel}
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-700 mb-1">{opp.campaignName}</p>
                <p className="text-xs text-slate-500 mb-3 flex-grow">{opp.description}</p>
                
                <div className="bg-slate-50 p-2 rounded text-xs mb-3 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Rate:</span>
                    <span className="font-semibold text-slate-800">{opp.rate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Min. Followers:</span>
                    <span className="font-semibold text-slate-800">{opp.minFollowers.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Zones:</span>
                    <span className="font-semibold text-slate-800">{opp.placementZones.join(", ")}</span>
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-auto"
                  onClick={() => handleApply(opp)}
                >
                  Apply Now
                </Button>
              </Card>
            ))}
          </div>
        </div>

        {/* Direct Sales Logic */}
        <Card className="mb-8 bg-gradient-to-r from-purple-50 to-white border-purple-100">
          <div className="flex flex-col @md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                Direct Sales Program <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Premium</span>
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                Sell ad space (e.g., Home Feed Banner, Video Pre-roll) directly to your own sponsors. Keep 100% of the revenue after platform fees.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-right">
                <p className="text-xs text-slate-500">Your Followers</p>
                <p className="text-lg font-bold text-slate-800">12,500</p>
              </div>
              {12500 < 10000 ? (
                 <Button variant="secondary" disabled>
                   Requires 10k+ Followers
                 </Button>
              ) : (
                <Button 
                  variant={isDirectSaleEnabled ? "outline" : "primary"}
                  onClick={() => {
                    setIsDirectSaleEnabled(!isDirectSaleEnabled);
                    alert(isDirectSaleEnabled ? "Direct Sales disabled." : "Direct Sales enabled! Sponsors can now contact you directly.");
                  }}
                >
                  {isDirectSaleEnabled ? "Disable Direct Sales" : "Enable Direct Sales"}
                </Button>
              )}
            </div>
          </div>
          <div className="mt-4 text-xs text-slate-500 border-t border-purple-100 pt-2">
            * Creators with under 10k followers must pay a 10,000 ETB deposit to enable direct sales features.
          </div>
        </Card>

        {/* Expandable Campaign Table */}
        <div>
          <div className="flex flex-col @sm:flex-row @sm:items-center justify-between gap-4 mb-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Active Campaigns
            </h3>
            <Button variant="primary" onClick={onCreate} className="w-full @sm:w-auto justify-center">
              <PlusCircle size={16} className="mr-2" /> Add Campaign
            </Button>
          </div>
          
          {/* Mobile Card View (Visible on small containers) */}
          <div className="block @md:hidden space-y-4">
            {initialCampaigns.map((campaign) => (
              <div key={campaign.id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-medium text-slate-800">{campaign.name}</h4>
                    <p className="text-xs text-slate-500">{campaign.advertiser}</p>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full ${
                      campaign.status === "Active"
                        ? "bg-green-100 text-green-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {campaign.status}
                  </span>
                </div>
                
                <div className="text-sm text-slate-600 mb-3">
                  <span className="font-medium">Source:</span> {campaign.source}
                </div>
                
                <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRowExpand(campaign.id)}
                    className="text-slate-500"
                  >
                    {expandedRow === campaign.id ? "Hide Details" : "View Details"}
                  </Button>
                  
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => onEdit(campaign)}>
                      <Edit size={16} className="text-slate-600" />
                    </Button>
                    <Button variant="ghost" size="icon">
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </div>
                
                {/* Expanded Details for Mobile */}
                {expandedRow === campaign.id && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <h5 className="text-xs font-semibold text-slate-700 mb-2">Creatives</h5>
                    <div className="space-y-2">
                      {(campaign.creatives || []).map((creative) => (
                        <div key={creative.id} className="bg-slate-50 p-2 rounded text-xs">
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">{creative.adType}</span>
                            <span>{creative.revenueETB?.toLocaleString()} ETB</span>
                          </div>
                          <div className="text-slate-500">{creative.adZone}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop Table View (Hidden on small containers) */}
          <div className="hidden @md:block bg-white border border-slate-200 rounded-lg shadow-sm">
            <table className="w-full text-sm text-left text-slate-500">
              <thead className="text-xs text-slate-700 uppercase bg-slate-50">
                <tr>
                  <th scope="col" className="px-6 py-3 w-8"></th>
                  <th scope="col" className="px-6 py-3">
                    Campaign
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Source
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 w-20 text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {initialCampaigns.map((campaign) => (
                  <Fragment key={campaign.id}>
                    <tr className="bg-white border-b hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleRowExpand(campaign.id)}
                        >
                          {expandedRow === campaign.id ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </Button>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-800">
                          {campaign.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {campaign.advertiser}
                        </p>
                      </td>
                      <td className="px-6 py-4">{campaign.source}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`font-medium ${
                            campaign.status === "Active"
                              ? "text-green-500"
                              : "text-slate-500"
                          }`}
                        >
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Menu
                          as="div"
                          className="relative inline-block text-left"
                        >
                          <Menu.Button
                            as={Button}
                            variant="ghost"
                            size="sm"
                            className="w-8 h-8 p-0 data-[headlessui-state=open]:bg-slate-100"
                          >
                            <MoreVertical size={16} />
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute right-0 mt-2 w-32 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                              <div className="px-1 py-1">
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      onClick={() => onEdit(campaign)}
                                      className={`${
                                        active
                                          ? "bg-primary text-white"
                                          : "text-gray-900"
                                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                      <Edit className="mr-2 h-4 w-4" /> Edit
                                    </button>
                                  )}
                                </Menu.Item>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      className={`${
                                        active
                                          ? "bg-red-500 text-white"
                                          : "text-gray-900"
                                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                                    </button>
                                  )}
                                </Menu.Item>
                              </div>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      </td>
                    </tr>
                    {expandedRow === campaign.id && (
                      <tr className="bg-slate-50">
                        <td colSpan="5" className="p-4">
                          <div className="p-2 bg-white rounded-md border border-slate-200">
                            <h4 className="text-sm font-semibold text-slate-800 p-2">
                              Creatives Performance
                            </h4>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-slate-200">
                                  <th className="p-2 text-left">Ad Zone</th>
                                  <th className="p-2 text-left">Ad Type</th>
                                  <th className="p-2 text-right">
                                    Impressions
                                  </th>
                                  <th className="p-2 text-right">CTR</th>
                                  <th className="p-2 text-right">
                                    Revenue (ETB)
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {(campaign.creatives || []).map((creative) => (                                  <tr
                                    key={creative.id}
                                    className="border-b border-slate-100 last:border-b-0"
                                  >
                                    <td className="p-2 font-medium">
                                      {creative.adZone}
                                    </td>
                                    <td className="p-2">{creative.adType}</td>
                                    {/* ✨ FIX: Use default values to prevent crash ✨ */}
                                    <td className="p-2 text-right">
                                      {(
                                        creative.impressions || 0
                                      ).toLocaleString()}
                                    </td>
                                    <td className="p-2 text-right">
                                      {creative.ctr || "0.0%"}
                                    </td>
                                    <td className="p-2 text-right font-semibold">
                                      {(
                                        creative.revenueETB || 0
                                      ).toLocaleString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity */}
        <RecentActivityList
          title="Recent Ad Revenue Activity"
          transactions={adTransactions}
        />
      </div>
    </>
  );
};

export default AdCampaignsTab;
